import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import { MCard } from "components/Shared";
import { addIncidentNote, deleteIncidentNote, editIncidentNote } from "requests/customerService/index.ts";
import { CUSTOMER_SERVICE_INCIDENT_ADD, CUSTOMER_SERVICE_INCIDENT_UPDATE, CUSTOMER_SERVICE_INCIDENT_DELETE } from "typeConstants/rights";
import { dateTimeToString } from "components/Shared/Formatters";
import { userHasRight } from "selectors/rights";
import { DATA_TYPE_MULTI_LINE_STRING } from "typeConstants/dataTypes";


const IncidentAdditionalNotes = ({
  incident,
  canAdd,
  canUpdate,
  canDelete,
  viewOnly,
}) => {
  const mounted = useRef(true);

  const [incidentId, setIncidentId] = useState(null);
  const [notes, setNotes] = useState([]);
  const [currentNoteText, setCurrentNoteText] = useState("");
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);

  useEffect(() => {
    return (() => { mounted.current = false; });
  }, []);

  useEffect(() => {
    if(incident.id !== incidentId) {
      setIncidentId(incident.id);
      setNotes(incident.notes.slice(0)); // using a copy so we don't violate the 'immutable props' principle
    }      
  }, [incident.id]);

  const deleteNote = (id) => {
    const nextNotes = notes.slice(0);
    const remove = nextNotes.findIndex(item => item.id === id);
    nextNotes.splice(remove, 1);
    setNotes(nextNotes);

    deleteIncidentNote(incident.id, id, null, null, mounted);
  };

  const submitNote = (id, note) => {
    setIsSubmittingNote(true);

    const finished = () => {
      setIsSubmittingNote(false);
      setCurrentNoteText("");
    };

    if (id > 0) {
      editIncidentNote(incident.id, id, {
        note: note,
      },
      () => {
        const nextNotes = notes.slice(0);
        const index = nextNotes.findIndex(n => n.id === id);
        nextNotes[index].note = note;
        setNotes(nextNotes);
        finished();
      },
      finished, mounted);
    }
    else {
      addIncidentNote(incident.id, {
        note: note,
      },
      data => {
        const nextNotes = notes.slice(0);
        nextNotes.unshift(data);
        setNotes(nextNotes);
        finished();
      },
      finished, mounted);
    }
  };

  const getFields = (note) => {
    return [
      { key: "note", dataType: DATA_TYPE_MULTI_LINE_STRING, label: "", value: note }
    ];
  };

  const getActions = (id) => {
    const actions = [];

    if (canDelete) {
      actions.push({
        icon: "delete_forever",
        helpText: "Delete",
        order: 9005,
        onClick: () => deleteNote(id)
      });
    }

    return actions;
  };

  return (
    <div>
      <h3>Notes</h3>
      {!isSubmittingNote && canAdd && !viewOnly &&
        <div style={{display:"flex", marginBottom:"12px", alignItems:"flex-end"}}>
          <TextField
            id={"incident-add-note"}
            value={currentNoteText}
            onChange={e => setCurrentNoteText(e.target.value)}
            style={{flexGrow:"1"}}
            placeholder={"Add New Note..."}
            variant="standard"
          />
          <Button
              style={{margin:"5px"}}
              onClick={() => submitNote(0, currentNoteText)}>Add Note</Button>
        </div>
      }
      {!isSubmittingNote &&
        <div>
         {notes.map(({ id, note, createdDate, createdByUser }) => (
            <MCard 
              key={id}
              title={`${createdByUser} - ${dateTimeToString(createdDate)}`}
              singleColumn
              isEditable={!viewOnly && canUpdate}
              fields={getFields(note)}
              actions={!viewOnly ? getActions(id) : null}
              onEditFinished={(data) => {if (data.length) submitNote(id, data[0].value);}}
            />
          ))}
        </div>
      }
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    canAdd: userHasRight(CUSTOMER_SERVICE_INCIDENT_ADD)(state),
    canUpdate: userHasRight(CUSTOMER_SERVICE_INCIDENT_UPDATE)(state),
    canDelete: userHasRight(CUSTOMER_SERVICE_INCIDENT_DELETE)(state),
  };
};

IncidentAdditionalNotes.propTypes = {
  incident: PropTypes.object.isRequired,

  // From state
  canAdd: PropTypes.bool,                             // If true, the user will be able to add notes
  canUpdate: PropTypes.bool,                          // If true, the user will be able to edit notes
  canDelete: PropTypes.bool,                          // If true, the user will be able to delete notes
  viewOnly: PropTypes.bool,                           // If true, the notes are read only
};

export default connect(mapStateToProps, null)(IncidentAdditionalNotes);
